import { serverConfig } from '@/config/server';
import type { FacilitiesResponse, Facility, FacilityFilters } from '@/types';

export interface FacilityFilterOptions {
  readonly statuses: readonly string[];
  readonly provinces: readonly string[];
  readonly facility_types: readonly string[];
}

function appendArrayParam(
  params: URLSearchParams,
  key: string,
  values?: readonly string[]
): void {
  if (values && values.length > 0) {
    // Use repeated params for array values (e.g., status=a&status=b)
    values.forEach((value) => {
      params.append(key, value);
    });
  }
}

class FacilityService {
  private readonly baseUrl: string;

  public constructor() {
    this.baseUrl = serverConfig.baseUrl;
  }

  public async fetchFacilities(
    filters?: FacilityFilters,
    page = 1,
    pageSize = 50
  ): Promise<FacilitiesResponse> {
    // If there's a search query, use the search endpoint
    if (filters?.search) {
      return this.searchFacilities(filters, pageSize);
    }

    const params = new URLSearchParams();
    const offset = (page - 1) * pageSize;
    params.set('limit', pageSize.toString());
    params.set('offset', offset.toString());

    appendArrayParam(params, 'province', filters?.provinces);
    appendArrayParam(params, 'status', filters?.statuses);
    appendArrayParam(params, 'facility_type', filters?.facility_types);

    if (filters?.sort_by) {
      params.set('sort_by', filters.sort_by);
    }

    if (filters?.sort_order) {
      params.set('sort_order', filters.sort_order);
    }

    const response = await fetch(
      `${this.baseUrl}/facilities?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch facilities: ${response.statusText}`);
    }

    const data = await response.json();

    // API returns array directly
    if (Array.isArray(data)) {
      return {
        facilities: data,
        total:
          data.length < pageSize ? offset + data.length : offset + pageSize + 1,
        page,
        page_size: pageSize,
      };
    }

    return data as FacilitiesResponse;
  }

  public async fetchFacilityById(id: string): Promise<Facility> {
    const response = await fetch(`${this.baseUrl}/facilities/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch facility: ${response.statusText}`);
    }

    return response.json();
  }

  public async fetchFilterOptions(): Promise<FacilityFilterOptions> {
    const response = await fetch(`${this.baseUrl}/facilities/filters`);

    if (!response.ok) {
      // Return empty defaults if endpoint not implemented yet
      if (response.status === 404) {
        return {
          statuses: [],
          provinces: [],
          facility_types: [],
        };
      }
      throw new Error(`Failed to fetch filter options: ${response.statusText}`);
    }

    return response.json();
  }

  private async searchFacilities(
    filters: FacilityFilters,
    limit = 50
  ): Promise<FacilitiesResponse> {
    const params = new URLSearchParams();
    params.set('q', filters.search || '');
    params.set('limit', limit.toString());

    appendArrayParam(params, 'province', filters.provinces);
    appendArrayParam(params, 'status', filters.statuses);
    appendArrayParam(params, 'facility_type', filters.facility_types);

    if (filters.sort_by) {
      params.set('sort_by', filters.sort_by);
    }

    if (filters.sort_order) {
      params.set('sort_order', filters.sort_order);
    }

    const response = await fetch(
      `${this.baseUrl}/facilities/search?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search facilities: ${response.statusText}`);
    }

    const data = await response.json();
    const facilities = Array.isArray(data) ? data : data.facilities || [];

    return {
      facilities,
      total: facilities.length,
      page: 1,
      page_size: limit,
    };
  }
}

export const facilityService = new FacilityService();
